import React, { useCallback, useEffect, useState } from 'react'
import {
  Divider,
  Header,
  Icon,
  Switch,
  Card,
  Grid,
  GridContainer,
  Row,
  Heading,
  Text,
  Spinner,
  Banner,
} from '@plurall/elo-beta'
import MainMenu from 'App/components/MainMenu/MainMenu'
import { useMSClient, usePlurallTracker } from 'hooks'
import { USER_PRIVACY_PREFERENCES_CHANNELS } from 'utils'
import styles from './Communications.module.scss'

const Communications = () => {
  const { getPrivacyPreferences, togglePrivacyPreferences } = useMSClient()
  const { sendTracker } = usePlurallTracker()

  const [userHasSchoolBlock, setUserHasSchoolBlock] = useState(false)
  const [emailOptIn, setEmailOptIn] = useState(true)
  const [whatsappOptIn, setWhatsappOptIn] = useState(true)
  const [pushNotificationsOptIn, setPushNotificationsOptIn] = useState(true)
  const [inAppOptIn, setInAppOptIn] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingToggleEmail, setLoadingToggleEmail] = useState(false)
  const [loadingToggleWhatsapp, setLoadingToggleWhatsapp] = useState(false)
  const [loadingTogglePushNotifications, setLoadingTogglePushNotifications] = useState(false)
  const [loadingToggleInApp, setLoadingToggleInApp] = useState(false)

  const privacyPreferencesChannels = [
    {
      icon: 'Email',
      title: 'E-mail',
      value: USER_PRIVACY_PREFERENCES_CHANNELS.email,
      optIn: emailOptIn,
      setOptIn: setEmailOptIn,
      loading: loadingToggleEmail,
      setLoading: setLoadingToggleEmail,
    },
    {
      title: 'Whatsapp',
      value: USER_PRIVACY_PREFERENCES_CHANNELS.whatsapp,
      optIn: whatsappOptIn,
      setOptIn: setWhatsappOptIn,
      loading: loadingToggleWhatsapp,
      setLoading: setLoadingToggleWhatsapp,
    },
    {
      icon: 'Bell',
      title: 'Central de notificações',
      value: USER_PRIVACY_PREFERENCES_CHANNELS.inApp,
      optIn: inAppOptIn,
      setOptIn: setInAppOptIn,
      loading: loadingToggleInApp,
      setLoading: setLoadingToggleInApp,
    },
    {
      icon: 'Phone',
      title: 'Alertas do aplicativo',
      value: USER_PRIVACY_PREFERENCES_CHANNELS.pushNotifcations,
      optIn: pushNotificationsOptIn,
      setOptIn: setPushNotificationsOptIn,
      loading: loadingTogglePushNotifications,
      setLoading: setLoadingTogglePushNotifications,
    },
  ]

  const fetchPrivacyPreferences = useCallback(async () => {
    try {
      const {
        me: { privacyPreferences },
      } = await getPrivacyPreferences()

      setUserHasSchoolBlock(privacyPreferences.hasSchoolBlock)

      const hasOptoutEmail = privacyPreferences.channels.some(
        channelData =>
          channelData.channel === USER_PRIVACY_PREFERENCES_CHANNELS.email &&
          channelData.optOutDate !== null,
      )

      const hasOptoutWhatsapp = privacyPreferences.channels.some(
        channelData =>
          channelData.channel === USER_PRIVACY_PREFERENCES_CHANNELS.whatsapp &&
          channelData.optOutDate !== null,
      )

      const hasOptoutPushNotifications = privacyPreferences.channels.some(
        channelData =>
          channelData.channel === USER_PRIVACY_PREFERENCES_CHANNELS.pushNotifcations &&
          channelData.optOutDate !== null,
      )

      const hasOptoutInApp = privacyPreferences.channels.some(
        channelData =>
          channelData.channel === USER_PRIVACY_PREFERENCES_CHANNELS.inApp &&
          channelData.optOutDate !== null,
      )

      setEmailOptIn(!(hasOptoutEmail || privacyPreferences.hasSchoolBlock))

      setWhatsappOptIn(!(hasOptoutWhatsapp || privacyPreferences.hasSchoolBlock))

      setPushNotificationsOptIn(!(hasOptoutPushNotifications || privacyPreferences.hasSchoolBlock))

      setInAppOptIn(!(hasOptoutInApp || privacyPreferences.hasSchoolBlock))
    } catch (error) {
      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'negative',
        text: 'Erro ao carregar dados das preferências de comunicação',
      })
    } finally {
      setLoading(false)
    }
  }, [getPrivacyPreferences])

  const toggleUserPrivacyPreference = useCallback(
    async channel => {
      const beforeUnload = event => {
        event.preventDefault()
      }

      window.addEventListener('beforeunload', beforeUnload)

      try {
        channel.setLoading(true)

        await togglePrivacyPreferences(channel.value)

        sendTracker('channel-preferences', {
          status: !channel.optIn === true ? 'on' : 'off',
          channel: channel.value.toLocaleLowerCase(),
        })

        channel.setOptIn(!channel.optIn)

        window.PLURALL_ELO_TOAST_CONTAINER.addToast({
          variant: 'positive',
          text: `Suas preferências para ${channel.title} foram atualizadas.`,
        })
      } catch (error) {
        window.PLURALL_ELO_TOAST_CONTAINER.addToast({
          variant: 'negative',
          text: `Não foi possível salvar as preferências para ${channel.title}. Por favor, tente novamente.`,
        })
      } finally {
        window.removeEventListener('beforeunload', beforeUnload)

        channel.setLoading(false)
      }
    },
    [togglePrivacyPreferences, sendTracker],
  )

  useEffect(() => {
    fetchPrivacyPreferences()
  }, []) // eslint-disable-line

  return (
    <>
      <Header description='Gerencie suas preferências de comunicação.'>Comunicações</Header>
      <GridContainer>
        <Row>
          <MainMenu />
          <Grid desktop={8} mobile={12}>
            <Card>
              <div className={styles.card}>
                <div className={styles.heading}>
                  <Heading noParagraph element='h5'>
                    Preferências de comunicação
                  </Heading>

                  <Text size='t3' shy>
                    Escolha em quais canais você deseja receber novidades e outras informações sobre
                    o Plurall.
                  </Text>
                </div>

                {loading ? (
                  <div className={styles.spinner}>
                    <Spinner />
                  </div>
                ) : (
                  <>
                    {userHasSchoolBlock && (
                      <div data-test-id='banner-school-block' className={styles.banner}>
                        <Banner secondary title='Comunicações desabilitadas.' variant='attention'>
                          A sua escola optou por não receber essas comunicações.
                        </Banner>
                      </div>
                    )}

                    {privacyPreferencesChannels.map(channel => (
                      <>
                        <div className={styles['channel-container']}>
                          <div className={styles['channel-title']}>
                            {channel.value === USER_PRIVACY_PREFERENCES_CHANNELS.whatsapp ? (
                              <img
                                alt='Logo do whatsapp'
                                src={`${process.env.REACT_APP_ASSETS_URL}/images/account/whatsapp-logo.svg`}
                                className={styles['whatsapp-logo']}
                              />
                            ) : (
                              <Icon type={channel.icon} shy />
                            )}

                            <Text>{channel.title}</Text>
                          </div>

                          <div className={styles['switch-container']}>
                            {channel.loading && <Spinner />}

                            <Switch
                              dataTestId={`toggle-${channel.title}`}
                              size='large'
                              checked={channel.optIn}
                              onChange={() =>
                                !userHasSchoolBlock &&
                                !channel.loading &&
                                toggleUserPrivacyPreference(channel)
                              }
                              disabled={channel.loading || userHasSchoolBlock}
                            />
                          </div>
                        </div>

                        {channel !==
                          privacyPreferencesChannels[privacyPreferencesChannels.length - 1] && (
                          <Divider light />
                        )}
                      </>
                    ))}
                  </>
                )}
              </div>
            </Card>

            <div className={styles.description}>
              <Text size='t4' dimmed>
                Você continuará recebendo comunicações importantes sobre sua conta e seu uso da
                plataforma.
              </Text>
            </div>
          </Grid>
        </Row>
      </GridContainer>
    </>
  )
}

export default Communications
